import React, { useState, useRef, useEffect } from 'react';
import * as Tone from 'tone';

const RealisticVoiceChanger = () => {
  const [pitch, setPitch] = useState(0);     // semitones
  const [speed, setSpeed] = useState(1);     // 0.5x → 2x
  const [formant, setFormant] = useState(0); // semitones (the realism key)
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fileName, setFileName] = useState("");

  const playerRef = useRef(null);

  const updateParams = () => {
    if (!playerRef.current) return;
    
    // === REALISTIC FORMANT MATH ===
    const formantRatio = Math.pow(2, formant / 12);
    playerRef.current.playbackRate = speed * formantRatio;
    playerRef.current.detune = (pitch - formant) * 100;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);

    const url = URL.createObjectURL(file);

    if (playerRef.current) playerRef.current.dispose();

    playerRef.current = new Tone.GrainPlayer(url, () => {
      setIsLoaded(true);
      updateParams(); // apply current settings
    }).toDestination();

    // Optimized for natural voice (smoother than default)
    playerRef.current.grainSize = 0.05;   // smaller = smoother speech
    playerRef.current.overlap = 0.08;     // better crossfade
  };

  const togglePlay = async () => {
    if (Tone.context.state !== 'running') await Tone.start();
    
    if (isPlaying) {
      playerRef.current.stop();
    } else {
      playerRef.current.start();
    }
    setIsPlaying(!isPlaying);
  };

  const changePitch = (val) => {
    const p = parseFloat(val);
    setPitch(p);
    updateParams();
  };

  const changeSpeed = (val) => {
    const s = parseFloat(val);
    setSpeed(s);
    updateParams();
  };

  const changeFormant = (val) => {
    const f = parseFloat(val);
    setFormant(f);
    updateParams();
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (playerRef.current) playerRef.current.dispose();
    };
  }, []);

  return (
    <div style={styles.card}>
      <h2 style={styles.title}>🎤 Realistic Voice Changer</h2>
      <p style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', marginBottom: '15px' }}>
        Pitch + Speed + <strong>Formant</strong> (real gender/character change)
      </p>
      
      <div style={styles.uploadBox}>
        <input type="file" accept="audio/*" onChange={handleFileUpload} id="file-upload" hidden />
        <label htmlFor="file-upload" style={styles.uploadBtn}>
          {fileName ? fileName : "Upload Voice/Audio File"}
        </label>
      </div>

      {isLoaded && (
        <div style={styles.controls}>
          {/* Pitch */}
          <div style={styles.controlGroup}>
            <div style={styles.labelRow}>
              <span>Pitch</span>
              <span style={styles.value}>{pitch > 0 ? `+${pitch}` : pitch} semitones</span>
            </div>
            <input 
              type="range" min="-12" max="12" step="1" 
              value={pitch} onChange={(e) => changePitch(e.target.value)}
              style={styles.slider}
            />
          </div>

          {/* Formant (the magic for realism) */}
          <div style={styles.controlGroup}>
            <div style={styles.labelRow}>
              <span>Formant Shift</span>
              <span style={styles.value}>{formant > 0 ? `+${formant}` : formant} semitones</span>
            </div>
            <input 
              type="range" min="-12" max="12" step="1" 
              value={formant} onChange={(e) => changeFormant(e.target.value)}
              style={styles.slider}
            />
            <small style={{ color: '#666', fontSize: '0.75rem' }}>
              + = higher voice character (female) • - = deeper (male)
            </small>
          </div>

          {/* Speed */}
          <div style={styles.controlGroup}>
            <div style={styles.labelRow}>
              <span>Speed</span>
              <span style={styles.value}>{Math.round(speed * 100)}%</span>
            </div>
            <input 
              type="range" min="0.5" max="2.0" step="0.01" 
              value={speed} onChange={(e) => changeSpeed(e.target.value)}
              style={styles.slider}
            />
          </div>

          <button onClick={togglePlay} style={isPlaying ? styles.stopBtn : styles.playBtn}>
            {isPlaying ? "⏹ STOP" : "▶ PLAY"}
          </button>
        </div>
      )}
    </div>
  );
};

// Same professional styles as your original (slightly tweaked)
const styles = {
  card: {
    background: '#1a1a1a', color: '#fff', padding: '30px', borderRadius: '15px',
    width: '380px', margin: '50px auto', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', 
    fontFamily: 'sans-serif'
  },
  title: { textAlign: 'center', marginBottom: '10px', fontSize: '1.3rem', color: '#00ffa3' },
  uploadBox: { marginBottom: '20px', textAlign: 'center' },
  uploadBtn: {
    background: '#333', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', 
    display: 'inline-block', border: '1px solid #444', fontSize: '1rem'
  },
  controls: { display: 'flex', flexDirection: 'column', gap: '28px' },
  controlGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
  labelRow: { display: 'flex', justifyContent: 'space-between', fontSize: '0.95rem', color: '#aaa' },
  value: { color: '#00ffa3', fontWeight: 'bold' },
  slider: { width: '100%', cursor: 'pointer', accentColor: '#00ffa3' },
  playBtn: {
    background: '#00ffa3', color: '#000', border: 'none', padding: '14px', borderRadius: '8px',
    fontWeight: 'bold', cursor: 'pointer', marginTop: '10px', fontSize: '1.1rem'
  },
  stopBtn: {
    background: '#ff4b4b', color: '#fff', border: 'none', padding: '14px', borderRadius: '8px',
    fontWeight: 'bold', cursor: 'pointer', marginTop: '10px', fontSize: '1.1rem'
  }
};

export default RealisticVoiceChanger;